import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { useNotificationMessage } from 'src/common/notification/message.hook';
import { useCurrentUser } from 'src/store/user';
import { apiUpdateUser } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { Helmet } from 'react-helmet';
import { Button } from 'src/common/button';
import { RadioGroup } from 'src/common/form/radio-group';
import { TextInput } from 'src/common/form/text-input';
import { apiSegmentTrackAnonymous } from 'src/utils/segment';

const STORY_OPTIONS = [
  { name: 'B2B services', code: 'b2b_services' },
  { name: 'Marketing agency', code: 'marketing_agency' },
  { name: 'SaaS', code: 'saas' },
  { name: 'Consulting services', code: 'consulting' },
  { name: 'Venture capital/Private Equity', code: 'vc_pe' },
  { name: 'Education', code: 'education' },
  { name: 'Other', code: 'other' }
];

const OTHER_STORY_CODE = 'other';

export const PageOnboardingStory = () => {
  const history = useHistory();
  const { setErrorNotification } = useNotificationMessage();
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const setCurrentUser = useCurrentUser((state) => state.setCurrentUser);

  const [selectedStory, setSelectedStory] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [otherText, setOtherText] = useState('');

  const nextStep = useCallback(() => {
    if (currentOrganization.is_onboarded) {
      window.location.replace('/');
    } else {
      history.push('./purpose');
    }
  }, [currentOrganization.is_onboarded, history]);

  const onSubmit = async () => {
    try {
      setSubmitting(true);
      const params: Record<string, any> = {
        story_type: selectedStory,
        story_other: selectedStory === OTHER_STORY_CODE ? otherText : undefined
      };

      const updatedUser = await apiUpdateUser(params);
      if (updatedUser) {
        setSubmitting(false);
        setCurrentUser(updatedUser);
        apiSegmentTrackAnonymous('Story Type Selected', {
          story_type: selectedStory,
          story_other: selectedStory === OTHER_STORY_CODE ? otherText : '',
        });
        nextStep();
      }
    } catch (error: any) {
      setSubmitting(false);
      setErrorNotification(error.message);
    }
  };

  const submitDisabled =
    !selectedStory || (selectedStory === OTHER_STORY_CODE && !otherText.trim()) || submitting;

  return (
    <>
      <Helmet>
        <title>Journey | The Story</title>
      </Helmet>
      <div className='flex flex-col items-center w-full max-w-[500px] mx-auto px-6 py-12'>
        <div className='text-4xl font-bold text-center mb-12 text-bedrock-black'>The Story</div>
        <div className='w-full'>
          <div className='text-2xl font-semibold text-center mb-8 text-bedrock-black'>What's your story about?</div>
          <div className='flex flex-col gap-6'>
            <RadioGroup
              name='story'
              className='flex flex-col space-y-4'
              itemClassName='flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-journey-primary-blue hover:bg-gray-100 transition-all duration-200 cursor-pointer'
              options={STORY_OPTIONS.map(({ name, code }) => ({ label: name, value: code }))}
              value={selectedStory}
              onChange={(option) => setSelectedStory(option.value)}
            />
            {selectedStory === OTHER_STORY_CODE && (
              <TextInput
                id='story_other'
                placeholder='Please specify your story type'
                onChange={(e: any) => setOtherText(e.target.value)}
                autoFocus
                inputProps={{ 
                  value: otherText,
                  className: 'w-full p-3 border border-gray-200 rounded-lg focus:border-journey-primary-blue focus:ring-1 focus:ring-journey-primary-blue transition-all duration-200'
                }}
              />
            )}
          </div>
          <div className='flex mt-8'>
            <Button
              type='submit'
              variant='primary'
              size='regular'
              className='w-full py-4 text-lg font-semibold rounded-lg'
              onClick={onSubmit}
              disabled={submitDisabled}
              label='Next'
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PageOnboardingStory; 